
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { BoPanel } from 'bonomo';
import { namespace } from 'vuex-class';
import CommunityNav from '@/components/community/widget/CommunityNav.vue';
import CreateButton from '@/components/shared/CreateButton.vue';
import Datim from '@/components/shared/Datim.vue';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import { b2bInvitesService } from '@/services/b2bInvitesService';
import { communityService } from '@/services/communityService';
import { BetterDataTableHeader } from '@/model/betterVuetifyInterfaces';
import { IAuthUserData } from '@/store/modules/authModule';
import { PermissionHelper } from '@/helpers/PermissionHelper';
import { UrlHelper } from '@/helpers/UrlHelper';

const authModule = namespace('authModule');

const PERMISSION_LOCKER_OWNER = 101;
const PERMISSION_LOCKER_OPERATOR = 102;

@Component({
  name: 'CommunityB2bInvites',
  components: { BoPanel, CommunityNav, CreateButton, Datim, PaginationBar },
})
export default class CommunityB2bInvites extends Vue {
  @Prop()
  protected communityId!: number;

  @authModule.Getter('userData')
  protected authUser!: IAuthUserData;

  protected loading: boolean = true;
  protected invites: any[] = [];
  protected currentPage: number = 1;
  protected lastPage: number = 1;
  protected total: number = 0;
  protected selectedPerPage: number = 15;
  readonly perPageOptions: number[] = [15, 25, 50];

  protected createDialog: boolean = false;
  protected creating: boolean = false;
  protected createError: boolean = false;
  protected newPermission: number | null = null;
  protected deleting: string | null = null;
  protected snackbar: boolean = false;
  protected snackbarText: string = '';
  protected noOnlineLockers: boolean = false;
  protected noSendLink: boolean = false;


  get canCreate(): boolean {
    return PermissionHelper.hasRoleAdministrator(this.authUser);
  }

  get headers(): BetterDataTableHeader[] {
    return [
      { text: this.$t('communities.invitationLinks.fields.code') as string,       value: 'code',        sortable: false },
      { text: this.$t('communities.invitationLinks.fields.permission') as string,  value: 'permission',  sortable: false },
      { text: this.$t('communities.invitationLinks.fields.usedBy') as string,      value: 'usedBy',      sortable: false },
      { text: this.$t('communities.invitationLinks.fields.usedByEmail') as string, value: 'usedByEmail', sortable: false },
      { text: this.$t('common.createdAt') as string,                               value: 'createdAt',   sortable: true  },
      { text: '',                                                                    value: 'actions',     sortable: false, align: 'end' },
    ];
  }

  get permissionOptions() {
    return [
      { label: PERMISSION_LOCKER_OWNER + '. ' + this.$t('settings.permissions.permissions.101.title'), value: PERMISSION_LOCKER_OWNER },
      { label: PERMISSION_LOCKER_OPERATOR + '. ' + this.$t('settings.permissions.permissions.102.title'), value: PERMISSION_LOCKER_OPERATOR },
    ];
  }

  async loadData() {
    await Promise.all([
      this.fetchPage(this.currentPage),
      this.checkOnlineLockers(),
    ]);
  }

  async checkOnlineLockers() {
    try {
      const pagination = await communityService.fetchLockers(this.communityId, { per_page: 500 });
      const lockers: any[] = Array.isArray(pagination && pagination.data) ? pagination.data : [];
      this.noOnlineLockers = !lockers.some((l: any) => l.status === 'Online');
    } catch (_e) {
      // silent
    }
  }

  async fetchPage(page: number) {
    this.loading = true;
    try {
      const result = await b2bInvitesService.fetchByCommunity(this.communityId, page, this.selectedPerPage);
      this.invites = result.data;
      this.currentPage = result.current_page;
      this.lastPage = result.last_page;
      this.total = result.total;
    } finally {
      this.loading = false;
    }
  }

  onPageChange(page: number) {
    this.fetchPage(page);
  }

  onPerPageChange(perPage: number) {
    this.selectedPerPage = perPage;
    this.fetchPage(1);
  }

  async submitCreate() {
    if (!this.newPermission) return;
    this.creating = true;
    this.createError = false;
    try {
      await b2bInvitesService.create(this.communityId, this.newPermission);
      this.createDialog = false;
      this.newPermission = null;
      await this.fetchPage(1);
    } catch (_e) {
      this.createError = true;
    } finally {
      this.creating = false;
    }
  }

  async deleteInvite(item: any) {
    this.deleting = item.code;
    try {
      await b2bInvitesService.destroy(item.code);
      this.invites = this.invites.filter(i => i.code !== item.code);
      this.total = Math.max(0, this.total - 1);
    } catch (_e) {
      this.showSnackbar(this.$t('common.errorHappened') as string);
    } finally {
      this.deleting = null;
    }
  }

  copyCode(code: string) {
    navigator.clipboard.writeText(UrlHelper.b2bInviteRegister(code)).then(() => {
      this.showSnackbar(this.$t('communities.invitationLinks.copied') as string);
    });
  }

  private showSnackbar(text: string) {
    this.snackbarText = text;
    this.snackbar = true;
  }
}
